import React, { useCallback } from 'react';

import type { GasPayload } from '@atlaskit/analytics-gas-types';

import { SearchExperience } from '../../../../../common/constants/search-experience';
import { FILTER_CHANGE_LOCATION } from '../../../../../common/utils/filters/events';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../../controllers/analytics';
import { siteFilterId } from '../../../../../controllers/store/search/filters/filter-ids';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { useFilterAnalytics } from '../../../../quick-find/dialog/filters/inline-product-filters/utils';

import SiteSelectFilter from './site-select-filter';

/**
 * The Site Selector as mounted in the lite header.
 *
 * In the standard layout `ProductFilters` owns the filter row and passes `onFilterChange` into
 * every filter, so selections are reported centrally. The lite header renders the Site Selector on
 * its own, outside that row, so it supplies the same callback here rather than making the layout
 * components aware of filter analytics.
 */
export const LiteHeaderSiteSelectFilter = (): React.JSX.Element => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useCommonAndPrivateAttributes();

	const fireAnalyticsCb = useCallback(
		(payload: GasPayload) => {
			const { attributes, ...rest } = payload;
			fireAnalyticsEvent({
				...rest,
				attributes: {
					// Search-page needs to have attributes within additionalAttributes or else it gets ommitted when
					// the event is emitted
					additionalAttributes: {
						...attributes,
					},
				},
			});
		},
		[fireAnalyticsEvent],
	);

	const { onFilterChange } = useFilterAnalytics(
		fireAnalyticsCb,
		commonAttributes,
		SearchExperience.SEARCH_PAGE,
	);

	return (
		<SiteSelectFilter
			onFilterChange={(change) =>
				onFilterChange(siteFilterId, change, FILTER_CHANGE_LOCATION.INLINE_FPS)
			}
		/>
	);
};
